import React from 'react';
import type { Highlight, HighlightCTA, HighlightCTAAction } from '../../types';
import HighlightPolaroid from './HighlightPolaroid';
import HighlightPill from './HighlightPill';
import HighlightHelpNote from './HighlightHelpNote';
import HighlightLedger from './HighlightLedger';
import { parseEm } from './parseEm';
import { runSubscribe, runShare, runSave } from '../../utils/highlightActions';
import '../../styles/highlight/HighlightRight.css';

interface HighlightRightProps {
  data: Highlight;
  ariaTitle: string;
}

const HighlightRight: React.FC<HighlightRightProps> = ({ data, ariaTitle }) => (
  <div className="psl-hl-pg psl-hl-pg--right">
    <span className="psl-hl-clip" aria-hidden="true">
      <svg viewBox="0 0 34 80">
        <path
          d="M9 8 C 9 4, 14 2, 18 2 C 22 2, 25 4, 25 9 L 25 65 C 25 70, 22 73, 18 73 C 14 73, 11 70, 11 65 L 11 16 C 11 13, 13 12, 15 12 C 17 12, 19 13, 19 16 L 19 60"
          stroke="#0f2a4a"
          strokeWidth="2.5"
          fill="none"
          opacity=".75"
        />
      </svg>
    </span>

    <HighlightPolaroid data={data.polaroid} illustrationLabel={ariaTitle} />

    <div className="psl-hl-pills">
      {data.pills.map((pill, i) => (
        <HighlightPill
          key={`${pill.glyph}-${i}`}
          glyph={pill.glyph}
          label={pill.label}
          value={pill.value}
          index={i}
        />
      ))}
    </div>

    {data.campaign ? (
      <HighlightLedger data={data.campaign} />
    ) : (
      <HighlightHelpNote data={data.helpNote} />
    )}

    <div className="psl-hl-ctas">
      {data.ctas.map((cta, i) => {
        const className = ctaClassName(cta);

        if (cta.action) {
          return (
            <button
              key={i}
              type="button"
              className={className}
              onClick={() => dispatchCta(cta.action!, data)}
            >
              {cta.label}
            </button>
          );
        }

        if (cta.href) {
          const external = /^https?:\/\//i.test(cta.href);
          return (
            <a
              key={i}
              className={className}
              href={cta.href}
              {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
            >
              {cta.label}
            </a>
          );
        }

        return (
          <button key={i} type="button" className={className}>
            {cta.label}
          </button>
        );
      })}
    </div>

    <div className="psl-hl-pgno">
      {parseEm(`${data.pageNumber} · *dir.*`)}
    </div>
  </div>
);

function ctaClassName(cta: HighlightCTA): string {
  if (cta.kind === 'scribbled') return 'psl-hl-cta psl-hl-cta--scribbled';
  if (cta.kind === 'primary') return 'psl-hl-cta psl-hl-cta--primary';
  return 'psl-hl-cta psl-hl-cta--ghost';
}

function dispatchCta(action: HighlightCTAAction, data: Highlight): void {
  switch (action) {
    case 'subscribe':
      runSubscribe(data.event);
      return;
    case 'share':
      void runShare(data.event);
      return;
    case 'save':
      runSave(data.event, plainHighlightTitle(data));
      return;
  }
}

function plainHighlightTitle(data: Highlight): string {
  return [data.title.pre, data.title.accent, data.title.post].filter(Boolean).join('');
}

export default HighlightRight;
